import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Hook to manage command palette open/close state and keyboard shortcut.
 * Registers Ctrl+K / Cmd+K to toggle the palette.
 */
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const previousActiveElement = useRef<Element | null>(null);

  const open = useCallback(() => {
    previousActiveElement.current = document.activeElement;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Restore focus to the previously focused element
    if (previousActiveElement.current instanceof HTMLElement) {
      previousActiveElement.current.focus();
    }
    previousActiveElement.current = null;
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isOpen) {
          close();
        } else {
          open();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, open, close]);

  return { isOpen, open, close };
}
